"use client";

import { CentPrecisionMoney, Money, TypedMoney } from "@commercetools/platform-sdk";
import cn from "clsx";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";

import { tierColors, LoyaltyTier } from "@/common/constants/loyalty";
import { OpenIcon } from "@/common/icons";
import TierIcon from "@/common/icons/tier-icon";
import { useMe } from "@/features/account/hooks/use-me";
import { useFormattedPrice } from "@/features/product/hooks/use-price";
import { useStoreProduct } from "../contexts/product-context";

type LoyaltyPriceBadgeProps = {
  inputCashback?: Money | TypedMoney | CentPrecisionMoney;
  inputDiscount?: Money | TypedMoney | CentPrecisionMoney;
};

export const LoyaltyPriceBadge = ({ inputCashback, inputDiscount }: LoyaltyPriceBadgeProps) => {
  const t = useTranslations();
  const session = useSession();
  const { pricingResult } = useStoreProduct();
  const { data: customer } = useMe(session.data);
  const loyaltyTierKey = customer?.custom?.fields.loyaltyTierKey;

  const totalCashback = useFormattedPrice(inputCashback ?? pricingResult?.cashbackAmount?.total);
  const totalPrice = pricingResult?.listPrice.total;
  const salePrice = pricingResult?.salePrice.total;
  const discountTotal =
    salePrice && totalPrice && salePrice?.centAmount < totalPrice?.centAmount
      ? {
          centAmount: totalPrice?.centAmount - salePrice?.centAmount,
          currencyCode: salePrice.currencyCode,
        }
      : ({
          centAmount: 0,
          currencyCode: inputCashback?.currencyCode ?? salePrice?.currencyCode,
        } as Money);

  const discount = useFormattedPrice(inputDiscount ?? discountTotal);

  if (!customer || !loyaltyTierKey || loyaltyTierKey === LoyaltyTier.BLUE) return;

  const tierColor = tierColors[loyaltyTierKey];

  return (
    <div>
      <div
        className={cn("flex rounded-md rounded-b-none text-white justify-center p-1", {
          "bg-loyalty-goldText": loyaltyTierKey === LoyaltyTier.GOLD,
          "bg-loyalty-bronzeText": loyaltyTierKey === LoyaltyTier.BRONZE,
          "bg-loyalty-silverText": loyaltyTierKey === LoyaltyTier.SILVER,
          "bg-loyalty-platinumText": loyaltyTierKey === LoyaltyTier.PLATINUM,
        })}
      >
        <TierIcon
          tier={loyaltyTierKey}
          width="24px"
          height="24px"
          textColor={tierColor}
          iconColor="#fff"
        />
        <div>
          <span className="uppercase text-xs font-semibold ml-1">
            {loyaltyTierKey} {t("common.text-prices")}
          </span>
        </div>
      </div>
      <div
        className="flex rounded-md rounded-t-none items-center justify-center p-1"
        style={{
          border: `1px solid ${tierColor}`,
        }}
      >
        <div className="p-2 px-5">
          <div className="font-bold text-base">{discount || 0}</div>
          <div className="text-xs">{t("common.text-saving")}</div>
        </div>
        <div className="p-2">
          <OpenIcon color={tierColor} width="24px" height="24px" />
        </div>
        <div className="p-2 px-5">
          <div className="font-bold text-base">{totalCashback}</div>
          <div className="text-xs">
            {t("common.text-cashback")}{" "}
            <abbr className="no-underline" title="required">
              *
            </abbr>
          </div>
        </div>
      </div>
    </div>
  );
};
